/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { manageLeaveActionRequest } from "../../redux/action/manageLeaveAction";
import { Link, useLocation } from "react-router-dom";
import { postApiCall } from "../../service/PostApiCall";
import { LEAVE_DELETE, LEAVEMANAGEHELPER, LEAVE_UPDATE } from "../../service/apiUrl";
import Utils from "../../utility/utility";
import moment from "moment";
import { FaBalanceScaleLeft } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";
import DataTable from "../../components/table/DataTable";
import DateField from "../../components/DateField";
import MorphTabs from "../../components/MorphTabs";
import {
  LeaveCell,
  LeaveToneLegend,
  leaveDateRange,
  leaveTone,
  statusKey,
} from "./leaveTone";

/* Status and period are two separate questions. They used to be one row of
   eight identical red buttons, where "Upcoming" and "Recent" were date ranges
   dressed as statuses, every button threw away the date and name filters, and
   turning the page silently dropped whichever one was picked. Now the status
   tabs, the period, the dates and the employee are all state that the one
   fetch below reads, so any combination holds ("pending, upcoming, for Asha")
   and survives paging and a trip to Edit and back. */

// Values are the API's status ids (ApiController::$leave_status).
const STATUS_TABS = [
  { value: "all", label: "All", countKey: "all" },
  { value: "4", label: "Pending", countKey: "pending", tone: "attention" },
  { value: "5", label: "Approved", countKey: "approved" },
  { value: "3", label: "On hold", countKey: "hold" },
  { value: "2", label: "Disapproved", countKey: "disApproved" },
  { value: "6", label: "Unsanctioned", countKey: "unsanction" },
  { value: "1", label: "Cancelled", countKey: "cancelled" },
];

const PERIODS = [
  { value: "month", label: "This month" },
  { value: "upcoming", label: "Upcoming" },
  { value: "recent", label: "Last 20 days" },
];

const periodRange = (period) => {
  switch (period) {
    case "upcoming":
      return [moment().startOf("day").toDate(), moment().add(3, "months").endOf("day").toDate()];
    case "recent":
      return [moment().subtract(20, "days").startOf("day").toDate(), moment().endOf("day").toDate()];
    default:
      return [moment().startOf("month").toDate(), moment().endOf("month").toDate()];
  }
};

const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    return null;
  }
};

/* Where the screen opens: a dashboard deep link (?fromDate&toDate&uid, unix
   seconds) wins, then what this screen saved last time, then this month. A
   named period is recomputed rather than restored, so "This month" saved in
   October opens as November's month in November. */
const initialFilters = (search) => {
  const query = new URLSearchParams(search);
  const saved = readJSON("leaveDateFilter") || {};
  const savedName = readJSON("filterName");
  const filters = {
    status: saved.status || "all",
    period: saved.period || "month",
    uid: query.get("uid") || savedName?.value || "",
  };

  if (query.get("fromDate") || query.get("toDate")) {
    const [from, to] = periodRange("month");
    filters.period = "custom";
    filters.from = query.get("fromDate") ? new Date(+query.get("fromDate") * 1000) : from;
    filters.to = query.get("toDate") ? new Date(+query.get("toDate") * 1000) : to;
  } else if (filters.period === "custom" && saved.fromDate && saved.toDate) {
    filters.from = new Date(saved.fromDate);
    filters.to = new Date(saved.toDate);
  } else {
    if (filters.period === "custom") filters.period = "month";
    [filters.from, filters.to] = periodRange(filters.period);
  }
  return filters;
};

/* Approve / Disapprove straight from the row. Only a leave still waiting on a
   decision offers them; anything already decided goes through Edit. */
const DECIDABLE = ["pending", "hold", "onhold"];
const DECISIONS = {
  approve: { status: "5", verb: "Approve", done: "Approved", tone: "primary" },
  disapprove: { status: "2", verb: "Disapprove", done: "Disapproved", tone: "danger" },
};

const rangeLabel = (from, to) => {
  const start = moment(from);
  const end = moment(to);
  return start.isSame(end, "year")
    ? `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`
    : `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

const ManageLeave = (props) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const leaveManage = useSelector((state) => state?.manageLeaveReducer);
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;

  const initial = useMemo(() => initialFilters(location.search), []);
  const [status, setStatus] = useState(initial.status);
  const [period, setPeriod] = useState(initial.period);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [uid, setUid] = useState(initial.uid);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = readJSON("leavePagination");
    return saved && !isNaN(saved.currentPage) ? parseInt(saved.currentPage, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);
  const [helperData, setHelperData] = useState({});
  const [deleteLeaveId, setDeleteLeaveId] = useState("");
  const [decision, setDecision] = useState(null); // { leave, kind }
  const [decisionNote, setDecisionNote] = useState("");

  const goToPage = (page) => {
    localStorage.setItem(
      "leavePagination",
      JSON.stringify({ type: "manage-leave", currentPage: page })
    );
    setCurrentPage(page);
  };

  useEffect(() => {
    postApiCall([], access_token, LEAVEMANAGEHELPER)
      .then((result) => setHelperData(result || {}))
      .catch((error) => console.error("LEAVEMANAGEHELPER", error));
  }, []);

  // A deep link followed while already on this screen.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const next = initialFilters(location.search);
    setPeriod(next.period);
    setFromDate(next.from);
    setToDate(next.to);
    setUid(next.uid);
    goToPage(1);
  }, [location.search]);

  const fromTs = Utils.fromDateTimeStamp(fromDate);
  const toTs = Utils.toDateTimeStamp(toDate);

  useEffect(() => {
    const params = {
      access_token: access_token,
      pageSize: pageSize,
      page: currentPage,
      uid: uid,
      start_time: fromTs,
      end_time: toTs,
    };
    if (status !== "all") params.status = status;

    localStorage.setItem(
      "leaveDateFilter",
      JSON.stringify({ fromDate, toDate, status, period })
    );
    dispatch(manageLeaveActionRequest(params, access_token));
  }, [status, fromTs, toTs, uid, currentPage, pageSize, reloadKey]);

  const changeStatus = (next) => {
    setStatus(next);
    goToPage(1);
  };

  const changePeriod = (next) => {
    if (next === "custom") return;
    const [from, to] = periodRange(next);
    setPeriod(next);
    setFromDate(from);
    setToDate(to);
    goToPage(1);
  };

  const changeDates = (from, to) => {
    if (!from || !to) return;
    setPeriod("custom");
    setFromDate(from);
    setToDate(to);
    goToPage(1);
  };

  const changeEmployee = (option) => {
    if (option) {
      localStorage.setItem("filterName", JSON.stringify(option));
    } else {
      localStorage.removeItem("filterName");
    }
    setUid(option?.value ?? "");
    goToPage(1);
  };

  const isDefault = status === "all" && period === "month" && !uid;
  const resetFilters = () => {
    localStorage.removeItem("filterName");
    const [from, to] = periodRange("month");
    setStatus("all");
    setPeriod("month");
    setFromDate(from);
    setToDate(to);
    setUid("");
    goToPage(1);
  };

  const deleteSubmit = () => {
    return postApiCall({ id: deleteLeaveId, showSuccessToast: true }, access_token, LEAVE_DELETE).then(() => {
      setDeleteLeaveId("");
      setReloadKey((key) => key + 1);
    });
  };

  const openDecision = (leave, kind) => {
    setDecisionNote("");
    setDecision({ leave, kind });
  };

  /* leave-update takes a partial record: only `status` (and the note, when
     there is one) change. It stamps updated_by and sends the employee the
     status-change notification itself. */
  const submitDecision = () => {
    if (!decision) return false;
    const params = {
      id: decision.leave.id,
      status: DECISIONS[decision.kind].status,
      showSuccessToast: true,
    };
    if (decisionNote.trim()) params.comments = decisionNote.trim();
    return postApiCall(params, access_token, LEAVE_UPDATE).then(() => {
      setDecision(null);
      setReloadKey((key) => key + 1);
    });
  };

  const counts = leaveManage?.data?.extraDetails || {};
  const statusItems = STATUS_TABS.map((tab) => ({
    value: tab.value,
    label: tab.label,
    tone: tab.tone,
    count: counts[tab.countKey] ?? undefined,
  }));
  const periodItems =
    period === "custom" ? [...PERIODS, { value: "custom", label: "Custom" }] : PERIODS;

  const employeeOptions = helperData?.users || [];
  const employeeValue =
    employeeOptions.find((option) => String(option.value) === String(uid)) || null;

  const activeStatus = STATUS_TABS.find((tab) => tab.value === status);
  const pendingCount = Number(counts.pending) || 0;

  const rows = leaveManage?.data?.leaves ?? [];

  /* Server mode: one page of leaves; the filters belong to the API. Ten
     columns did not fit the card (Manage was clipped off the right edge), so
     type, duration and time frame are one "Leave" cell, and the applied date
     sits under the name it belongs to. */
  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "#",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "56px" },
        cell: ({ row }) => (
          <span className="text-nowrap text-muted">
            {(currentPage - 1) * pageSize + row.index + 1}
          </span>
        ),
      },
      {
        id: "name",
        header: "Employee",
        enableHiding: false,
        cell: ({ row }) => (
          <div>
            <div className="fw-medium text-nowrap">{row.original?.user?.name}</div>
            {Number(row.original?.created_at) ? (
              <div className="text-muted text-nowrap" style={{ fontSize: "12px" }}>
                Applied {moment.unix(Number(row.original.created_at)).format("MMM D, YYYY")}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: "leave",
        header: "Leave",
        cell: ({ row }) => <LeaveCell leave={row.original} />,
      },
      {
        id: "leave_date",
        header: "Dates",
        cell: ({ row }) => <span className="text-nowrap">{leaveDateRange(row.original)}</span>,
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <span className={"status-pill s-" + statusKey(row.original?.status)}>
            {row.original?.status}
          </span>
        ),
      },
      {
        id: "updated_by",
        header: "Updated by",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.updated_by?.name || "-"}</span>
        ),
      },
      {
        id: "manage",
        header: "",
        enableSorting: false,
        enableHiding: false,
        meta: { className: "text-right" },
        cell: ({ row }) => (
          <div className="row-actions ml-row-actions">
            {DECIDABLE.includes(statusKey(row.original?.status)) ? (
              <>
                <button
                  type="button"
                  className="ml-act ml-act-approve"
                  onClick={() => openDecision(row.original, "approve")}
                >
                  <i className="fa fa-check" aria-hidden="true"></i>
                  Approve
                </button>
                <button
                  type="button"
                  className="ml-act ml-act-disapprove"
                  onClick={() => openDecision(row.original, "disapprove")}
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                  Disapprove
                </button>
              </>
            ) : null}
            <Link
              to={`/edit-leave/${row.original?.id}?uid=${row.original?.uid}`}
              aria-label="Edit leave"
              title="Edit"
            >
              <i className="fa fa-pencil site-color" aria-hidden="true"></i>
            </Link>
            <button
              type="button"
              aria-label="Delete leave"
              title="Delete"
              onClick={() => setDeleteLeaveId(row.original?.id)}
            >
              <i className="site-color fa fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  const emptyText =
    status === "all"
      ? "No leaves in this period"
      : `No ${activeStatus?.label.toLowerCase()} leaves in this period`;

  return (
    <>
      <div className={props?.data?.transtion == true ? "contents" : "content"}>
        <div className="content-top-spacer"></div>
        <div className="color-body">
          <div className="page-head page-head-row">
            <div>
              <div className="page-eyebrow">People</div>
              <h1 className="page-title">Manage Leave</h1>
              <p className="page-sub">
                {rangeLabel(fromDate, toDate)}
                {pendingCount
                  ? ` · ${pendingCount} awaiting approval`
                  : " · nothing awaiting approval"}
              </p>
            </div>
            <div className="page-head-actions">
              <Link to="/leave-balance" className="dt-btn">
                <FaBalanceScaleLeft aria-hidden="true" />
                Leave balance
              </Link>
              <Link to="/add-leave" className="dt-btn dt-btn-primary">
                <i className="fa fa-plus" aria-hidden="true"></i>
                Add leave
              </Link>
            </div>
          </div>
          <div className="col-md-12">
            <div className="card-input mt-3">
              <div className="col-md-12 mb-3">
                <div className="mt-4">
                  <div className="ml-statusbar">
                    <MorphTabs
                      label="Leave status"
                      items={statusItems}
                      value={status}
                      onChange={changeStatus}
                    />
                    <LeaveToneLegend />
                  </div>
                  <DataTable
                    columns={columns}
                    data={rows}
                    isLoading={leaveManage?.isLoading}
                    getRowId={(row, index) => String(row?.id ?? index)}
                    sorting={false}
                    rowTone={(row) => leaveTone(row.original)}
                    filters={
                      <div className="ml-filters">
                        <div className="filter-cell">
                          <span className="filter-label">Period</span>
                          <MorphTabs
                            size="sm"
                            label="Period"
                            items={periodItems}
                            value={period}
                            onChange={changePeriod}
                          />
                        </div>
                        <div className="filter-cell ml-date">
                          <span className="filter-label">From</span>
                          <DateField
                            value={fromDate}
                            maxDate={toDate}
                            onChange={(date) => changeDates(date, toDate)}
                          />
                        </div>
                        <span className="ml-range-sep" aria-hidden="true">
                          -
                        </span>
                        <div className="filter-cell ml-date">
                          <span className="filter-label">To</span>
                          <DateField
                            value={toDate}
                            minDate={fromDate}
                            onChange={(date) => changeDates(fromDate, date)}
                          />
                        </div>
                        <div className="filter-cell ml-employee">
                          <span className="filter-label">Employee</span>
                          <Select
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            options={employeeOptions}
                            isSearchable={true}
                            isClearable={true}
                            value={employeeValue}
                            placeholder="All employees"
                            onChange={changeEmployee}
                          />
                        </div>
                        {!isDefault ? (
                          <button type="button" className="ml-reset" onClick={resetFilters}>
                            Reset filters
                          </button>
                        ) : null}
                      </div>
                    }
                    pagination={{
                      manual: true,
                      pageIndex: currentPage - 1,
                      pageSize: pageSize,
                      pageCount: leaveManage?.data?.total_pages ?? -1,
                      pageSizeOptions: [10, 25, 50, 100],
                      onChange: (next) => {
                        // a new page size re-slices the whole result, so
                        // the page it was paired with is meaningless
                        const resized = next.pageSize !== pageSize;
                        goToPage(resized ? 1 : next.pageIndex + 1);
                        setPageSize(next.pageSize);
                      },
                    }}
                    emptyText={emptyText}
                  />

                  <ConfirmDialog
                    isShow={Boolean(decision)}
                    onHide={() => setDecision(null)}
                    title={
                      decision
                        ? `${DECISIONS[decision.kind].verb} ${decision.leave?.user?.name ?? "this"}'s leave?`
                        : ""
                    }
                    description={
                      decision
                        ? `${decision.leave?.type}${
                            decision.leave?.duration ? ` · ${decision.leave.duration}` : ""
                          }, ${leaveDateRange(decision.leave)}. ${
                            decision.leave?.user?.name ?? "The employee"
                          } is notified.`
                        : ""
                    }
                    confirmLabel={decision ? DECISIONS[decision.kind].verb : "Confirm"}
                    successLabel={decision ? DECISIONS[decision.kind].done : "Done"}
                    tone={decision ? DECISIONS[decision.kind].tone : "primary"}
                    onConfirm={submitDecision}
                  >
                    <label className="filter-label mb-1" htmlFor="ml-decision-note">
                      Note for the employee (optional)
                    </label>
                    <textarea
                      id="ml-decision-note"
                      className="form-control"
                      rows={2}
                      value={decisionNote}
                      onChange={(event) => setDecisionNote(event.target.value)}
                      placeholder={
                        decision?.kind === "disapprove"
                          ? "Why it was disapproved"
                          : "Anything they should know"
                      }
                    />
                  </ConfirmDialog>

                  <ConfirmDialog
                    isShow={Boolean(deleteLeaveId)}
                    onHide={() => setDeleteLeaveId("")}
                    title="Delete this leave?"
                    description="The leave record is removed from the listing. This cannot be undone."
                    confirmLabel="Delete"
                    successLabel="Deleted"
                    tone="danger"
                    onConfirm={deleteSubmit}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageLeave;
